
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import * as d3 from 'd3';
import styles from './AutoSearch.css';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { SetQuery } from '../../actions';
import { plural, placeTypeSort } from '../../utils/utils';
import { browserHistory } from 'react-router';
import SearchInput from '../SearchInput';
import SearchResults from '../SearchResults';
import withStyles from '../../decorators/withStyles';
import { containerKey, gaEvent } from "../../utils/utils";

/**
 * Returns a key representing the container of a placeID
 * @param links
 * @param placeID
 */
function linksKey(links, placeID) {
  const types = links.map(l => l.placeType);
  types.sort();
  types.push(placeID.split(".").slice(1).join("."));
  return types.join(",");
}


class AutoSearch extends Component {

  constructor(props) {
    super(props);
    this.state = {
      value: props.nav ? props.placeName : "",
      exactMatch: null,
      selectedIdx: 0,
      searchHits: [],
    }
  }

  static defaultProps = {
    limit: 5,
    defaultSelected: -1,
    nav: true,
    autofocus: false,
    videoLink: false,
    placeholder: "Enter a coastal place",
    scope: ""
  };

  // TODO: this is fairly ugly. It's coupled to the /api/search presentation of
  // related links for each search result.
  linkDataToLinks = () => {
    var links = [];
    // Get a deduplicated array of link keys corresponding to the search hits.
    var linkKeys = Array.from(new Set(
        this.state.searchHits
            .filter(hit => hit.related_links.length > 1)
            .map(hit => linksKey(hit.related_links, hit.id))
    ));
    if (linkKeys.length === 0) {
      return links;
    } else if (linkKeys.length === 1) {
      var place = this.state.searchHits.find(hit => linksKey(hit.related_links, hit.id) === linkKeys[0]);
      place.related_links.forEach(link => {
        links.push({
          pathname: `/${link.placeType}`,
          query: {container: place.geo_container.placeID},
          container: place.geo_container,
          text: `${plural(link.placeTypeName)}`,
          placeType: link.placeType
        });
      });
    } else {
      linkKeys.forEach(k => {
        var place = this.state.searchHits.find(hit => linksKey(hit.related_links, hit.id) === k);
        links.push({
          pathname: "/search",
          query: {q: place.geo_container.name},
          text: `More places in ${place.geo_container.name}`,
          placeType: null,
        })
      });
    }
    links.sort((a, b) => {
      return placeTypeSort(a.placeType) > placeTypeSort(b.placeType) ? 1 : -1
    });

    return links;
  };

  numLinks = () => {
    const hasLinks = this.state.searchHits.filter(k => k.related_links.length > 0);
    if (hasLinks.length === 1) {
      return hasLinks[0].related_links.length;
    } else {
      return hasLinks.length;
    }
  };

  componentWillReceiveProps(props) {
    // FIXME: Quick hack to prevent place directory pages from
    // showing up in the search box.
    this.setState({value: ""});
  }

  handleSubmit = (e) => {
    e.preventDefault();
    var hit = null;
    if (this.state.searchHits.length == 1) {
      hit = this.state.searchHits[0];
    } else if (this.state.exactMatch) {
      hit = this.state.exactMatch;
    }
    if (hit === null) {
      gaEvent('search', 'submit', 'autosearch-submit-nomatch', this.state.value);
      this.props.dispatch(SetQuery(this.state.value));
      browserHistory.push({pathname: "/search", query: {q: this.state.value}, state: {...this.props, q: this.state.value}});
    } else {
      gaEvent('search', 'submit', 'autosearch-submit-match', this.state.value);
      this.setState({value: hit.name, searchHits: []});
      browserHistory.push({
        pathname: `/${hit.placetype}/${hit.id}`,
        state: {place: {id: hit.id, type: hit.placetype}}
      });
    }
  };

  handleInputFocus = (e) => {
    this.setState({value: "", searchHits: [], selectedIdx: this.props.defaultSelected});
  };

  handleChange = async (e) => {
    // Set the value of the input
    this.setState({value: e.target.value, exactMatch: null});
    // Search for the current text
    if (e.target.value !== '') {
      const url = `/api/search?scope=${this.props.scope}&limit=${this.props.limit}&search=${encodeURIComponent(e.target.value)}`;
      const data = await d3.json(url);
      if (data.results) {
        this.setState({searchHits: data.results});
        if (this.state.selectedIdx > data.results.length - 1) {
          this.setState({selectedIdx: this.props.defaultSelected});
        }
      }
    } else {
      this.setState({searchHits: [], selectedIdx: this.props.defaultSelected});
    }
  };

  handleKeyDown = (e) => {
    // Tab completion; tab key code is 9
    if (e.keyCode == 9) {
      e.preventDefault();
    }
    if (e.keyCode == 9) {
      // FIXME: race condition here; this might be fired before hits come in.
      if (this.state.searchHits.length > 0) {
        var selectedHit = this.state.searchHits[this.state.selectedIdx];
        if (!selectedHit) {
          selectedHit = this.state.searchHits[0];
        }
        this.setState({exactMatch: selectedHit});
        this.setState({value: selectedHit.name, searchHits: [], selectedIdx: this.props.defaultSelected});
      }
    // Enter key
    } else if (e.keyCode == 13) {
      if (this.state.searchHits.length > 0) {
        var selectedHit = this.state.searchHits[this.state.selectedIdx];
        if (selectedHit) {
          this.setState({exactMatch: selectedHit});
          this.setState({value: selectedHit.name, searchHits: [], selectedIdx: this.props.defaultSelected});
        } else {
          var links = this.linkDataToLinks();
          console.log(this.state.searchHits.length);
          console.log(this.state.selectedIdx);
          var link = links[this.state.selectedIdx - this.state.searchHits.length];
          if (link) {
            gaEvent('search', 'submit', 'autosearch-enter', link.pathname);
            browserHistory.push(link);
            e.stopPropagation();
          }
        }
      }
    // Up arrow
    } else if (e.keyCode == 38 && this.state.selectedIdx > 0) {
      this.setState({selectedIdx: this.state.selectedIdx - 1})
    // Down arrow
    } else if (e.keyCode == 40 && this.state.selectedIdx < (this.state.searchHits.length + this.numLinks()) - 1) {
      this.setState({selectedIdx: this.state.selectedIdx + 1})
    }
  };

  handleClick = (e) => {
    this.setState({value: '', searchHits: [], selectedIdx: this.props.defaultSelected});
  };

  handleMouseOver = (idx) => {
    this.setState({selectedIdx: idx})
  };

  render() {
    return (
      <div id={this.props.nav ? "lockin-search" : ""} className="temp-open">
        <SearchInput
            autofocus={this.props.autofocus}
            value={this.state.value}
            handleFocus={this.handleInputFocus}
            handleSubmit={this.handleSubmit}
            handleKeyDown={this.handleKeyDown}
            handleChange={this.handleChange}
            placeholder={this.props.placeholder}
        />
        <SearchResults
          selectedIdx={this.state.selectedIdx}
          handleMouseOver={this.handleMouseOver}
          handleClick={this.handleClick}
          query={{level: this.props.waterLevel.level, unit: this.props.waterLevel.unit}}
          results={this.state.searchHits}
          links={this.linkDataToLinks()}/>
      </div>
    )
  }
}

export default compose(
  withStyles(styles),
  connect((state) => state),
)(AutoSearch);
